<template>
  <div>
    <div class="panel">
      <!-- Cabecera: volver + subcontratista -->
      <div class="flex justify-between items-center mb-4">
        <div class="flex items-center gap-3">
          <b-button size="is-small" type="is-light" icon-left="arrow-left" icon-pack="fas" @click="_GoBack">
            {{ $t('back') }}
          </b-button>
          <h2 class="text-lg font-semibold text-gray-700">
            {{ $t('subcontractor_certifications') }}<span v-if="partner"> · {{ partner.name }}</span>
          </h2>
        </div>
        <b-button
          type="is-primary"
          size="is-small"
          icon-left="plus"
          icon-pack="fas"
          :disabled="!hasFreeEntities"
          @click="_OpenAdd"
        >
          {{ $t('add_certification') }}
        </b-button>
      </div>

      <b-table :data="processes" :loading="isLoading" hoverable striped icon-pack="fas" class="text-sm cert-table">
        <b-table-column field="entity_type" :label="$t('entity')" v-slot="props">
          {{ $t(_EntityLabel(props.row.entity_type)) }}
        </b-table-column>

        <b-table-column field="certifies_by" :label="$t('certifies_by')" v-slot="props">
          {{ $t(_CertifiesByLabel(props.row.certifies_by)) }}
        </b-table-column>

        <b-table-column field="certify_scope" :label="$t('certification_type')" v-slot="props">
          {{ _CertifyScopeLabel(props.row) }}
        </b-table-column>

        <b-table-column field="document_language" :label="$t('document_language')" v-slot="props">
          {{ _DocumentLanguageLabel(props.row) }}
        </b-table-column>

        <b-table-column field="currency" :label="$t('select_currency')" v-slot="props">
          {{ props.row.currency || $t('project_currency_default') }}
        </b-table-column>

        <b-table-column field="period" :label="$t('period')" v-slot="props">
          {{ $t(_PeriodLabel(props.row.period)) }}
        </b-table-column>

        <b-table-column field="start_date" :label="$t('certification_start_date')" v-slot="props">
          {{ _FormatDate(props.row.start_date) }}
        </b-table-column>

        <b-table-column field="certification_day" :label="$t('certification_day')" centered width="120" v-slot="props">
          {{ props.row.certification_day || '-' }}
        </b-table-column>

        <b-table-column field="progress" :label="$t('percentage')" numeric width="120" v-slot="props">
          {{ Number(props.row.progress || 0).toFixed(2) }}%
        </b-table-column>

        <b-table-column field="emails_enabled" :label="$t('emails_enabled')" centered width="140" v-slot="props">
          <span
            class="emails-status"
            :class="props.row.emails_enabled ? 'emails-status--yes' : 'emails-status--no'"
          >
            {{ _ReminderLabel(props.row) }}
          </span>
        </b-table-column>

        <b-table-column field="actions" :label="$t('actions')" width="140" centered v-slot="props">
          <div class="cert-actions">
            <b-tooltip :label="$t('edit')" position="is-left">
              <button type="button" class="action-icon-btn action-icon-btn--edit" @click="_OpenEdit(props.row)">
                <b-icon pack="fas" icon="pen" size="is-small" />
              </button>
            </b-tooltip>
            <b-tooltip
              :label="props.row.emails_enabled ? $t('send_now') : $t('enable_emails_to_send_test')"
              position="is-left"
              multilined
            >
              <span class="inline-flex">
                <button
                  type="button"
                  class="action-icon-btn action-icon-btn--send"
                  :class="{ 'is-disabled': !props.row.emails_enabled }"
                  :disabled="!props.row.emails_enabled"
                  @click="_SendEmailNow(props.row)"
                >
                  <b-icon pack="fas" icon="envelope" size="is-small" />
                </button>
              </span>
            </b-tooltip>
            <b-tooltip :label="$t('delete')" position="is-left">
              <button type="button" class="action-icon-btn action-icon-btn--delete" @click="_ConfirmDelete(props.row)">
                <b-icon pack="fas" icon="trash" size="is-small" />
              </button>
            </b-tooltip>
          </div>
        </b-table-column>

        <template #empty>
          <div class="has-text-centered py-8">
            <p class="text-gray-500">{{ isLoading ? $t('loading') : $t('no_results') }}</p>
          </div>
        </template>
      </b-table>
    </div>

    <!-- Modal crear/editar certificación (EpcModalWrapper, dentro del propio componente) -->
    <subcontractor-certification-settings-modal
      v-if="settingsDialog.active"
      :active="settingsDialog.active"
      :title="settingsDialog.title"
      :project-id="projectId"
      :partner-id="partnerId"
      :process="settingsDialog.process"
      :used-entities="usedEntities"
      @saved="_OnSettingsSaved"
      @close="_CloseSettingsDialog"
    />

    <!-- Confirmación de envío de prueba (estilo Facebook / colores app) -->
    <b-modal
      :active.sync="sendDialog.active"
      :width="420"
      scroll="keep"
      :can-cancel="['escape', 'outside']"
      class="send-test-modal"
    >
      <div class="send-test-dialog">
        <header class="send-test-dialog__header">
          <h3 class="send-test-dialog__title">{{ $t('send_test_emails_title') }}</h3>
          <button type="button" class="send-test-dialog__close" @click="_CloseSendDialog" :aria-label="$t('cancel')">
            <b-icon pack="fas" icon="times" size="is-small" />
          </button>
        </header>

        <div class="send-test-dialog__body">
          <p class="send-test-dialog__text">
            {{ $t('send_test_emails_message') }}
          </p>
          <div class="send-test-dialog__warning">
            <b-icon pack="fas" icon="exclamation-triangle" size="is-small" />
            <span>{{ $t('send_test_emails_warning') }}</span>
          </div>
        </div>

        <footer class="send-test-dialog__footer">
          <button type="button" class="send-test-dialog__btn send-test-dialog__btn--cancel" @click="_CloseSendDialog">
            {{ $t('cancel') }}
          </button>
          <button type="button" class="send-test-dialog__btn send-test-dialog__btn--confirm" @click="_ConfirmSendDialog">
            {{ $t('send_test_emails_confirm') }}
          </button>
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import SubcontractorCertificationSettingsModal from '·/components/partners/subcontractor-certification-settings-modal.vue';

// Mapas de enum (int) → clave de traducción.
const ENTITY_LABELS = {
  1: 'entity_civil_works',
  2: 'entity_mechanical_assembly',
  3: 'entity_electrical_installation',
};
const CERTIFIES_LABELS = { 1: 'production', 2: 'quality' };
// PartnerProcessEntity::ElectricalInstallation = 3; PartnerProcessCertifyScope (1 parcial, 2 completo).
const ENTITY_ELECTRICAL = 3;
const CERTIFY_SCOPE_LABELS = { 1: 'certify_include_partial', 2: 'certify_complete_only' };
// Idioma del documento (código → clave de traducción del nombre del idioma).
const DOCUMENT_LANGUAGE_LABELS = { es: 'spanish', en: 'english', pt: 'portuguese', it: 'italian' };
const PERIOD_LABELS = {
  1: 'daily', 2: 'weekly', 3: 'biweekly', 4: 'monthly',
  5: 'quarterly', 6: 'semiannual', 7: 'annual', 8: 'bimonthly',
};
const ALL_ENTITIES = [1, 2, 3];

export default {
  name: 'ProjectPartnerCertifications',
  middleware: 'auth',
  title: 'subcontractor_certifications',
  menuOption: '0-2',

  components: {
    SubcontractorCertificationSettingsModal,
  },

  computed: {
    ...mapGetters({
      navData: 'app/navData',
    }),
    projectId() {
      return parseInt(this.navData.project_id);
    },
    usedEntities() {
      return this.processes.map(p => p.entity_type);
    },
    hasFreeEntities() {
      return ALL_ENTITIES.some(e => !this.usedEntities.includes(e));
    },
  },

  data: () => ({
    partnerId: null,
    partner: null,
    processes: [],
    isLoading: false,
    settingsDialog: {
      active: false,
      title: '',
      process: null,
    },
    sendDialog: {
      active: false,
      row: null,
    },
  }),

  created() {
    this.partnerId = parseInt(this.$route.params.partner_id);
    this._Load();
  },

  methods: {
    async _Load() {
      this.isLoading = true;
      try {
        const { data } = await axios.get(
          `/api/v2/project/${this.projectId}/partners/${this.partnerId}/certification-processes`
        );
        if (data && data.success) {
          this.partner = data.partner || null;
          this.processes = data.processes || [];
        }
      } catch (e) {
        this.$notify.error('error_loading_data');
      } finally {
        this.isLoading = false;
      }
    },

    _OpenAdd() {
      this.settingsDialog = {
        active: true,
        title: this.$t('add_certification'),
        process: null,
      };
    },

    _OpenEdit(row) {
      this.settingsDialog = {
        active: true,
        title: this.$t('edit'),
        process: row,
      };
    },

    _CloseSettingsDialog() {
      this.settingsDialog = {
        active: false,
        title: '',
        process: null,
      };
    },

    _OnSettingsSaved() {
      this._CloseSettingsDialog();
      this._Load();
    },

    _SendEmailNow(row) {
      if (!row || !row.emails_enabled) {
        return;
      }
      this.sendDialog = {
        active: true,
        row,
      };
    },

    _CloseSendDialog() {
      this.sendDialog = {
        active: false,
        row: null,
      };
    },

    _ConfirmSendDialog() {
      const row = this.sendDialog.row;
      this._CloseSendDialog();
      if (row) {
        this._DispatchSend(row);
      }
    },

    async _DispatchSend(row) {
      if (!row || !row.id) {
        return;
      }
      try {
        const { data } = await axios.post(
          `/api/v2/project/${this.projectId}/partners/${this.partnerId}/certification-processes/${row.id}/send-reminder`
        );
        if (data && data.success) {
          this.$notify.success('test_emails_sent', [data.count]);
        } else {
          this.$notify.error(data && data.message ? data.message : 'error');
        }
      } catch (e) {
        const msg = e && e.response && e.response.data && e.response.data.message;
        this.$notify.error(msg || 'error');
      }
    },

    _ConfirmDelete(row) {
      this.$buefy.dialog.confirm({
        title: this.$t('delete'),
        message: this.$t('are_you_sure'),
        confirmText: this.$t('delete'),
        cancelText: this.$t('cancel'),
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this._Delete(row),
      });
    },

    async _Delete(row) {
      try {
        const { data } = await axios.delete(
          `/api/v2/project/${this.projectId}/partners/${this.partnerId}/certification-processes/${row.id}`
        );
        if (data && data.success) {
          this.$notify.success('success');
          this._Load();
        } else {
          this.$notify.error('error');
        }
      } catch (e) {
        this.$notify.error('error');
      }
    },

    _GoBack() {
      this.$router.push({ name: 'project.admin.partners' });
    },

    _EntityLabel(value) {
      return ENTITY_LABELS[value] || '-';
    },
    _CertifiesByLabel(value) {
      return CERTIFIES_LABELS[value] || '-';
    },
    // Alcance parcial/completo: solo aplica a instalación eléctrica (metros); en el resto '-'.
    _CertifyScopeLabel(row) {
      if (!row || row.entity_type !== ENTITY_ELECTRICAL) {
        return '-';
      }
      const key = CERTIFY_SCOPE_LABELS[row.certify_scope] || 'certify_include_partial';
      return this.$t(key);
    },
    _PeriodLabel(value) {
      return PERIOD_LABELS[value] || '-';
    },
    // Idioma del documento: el configurado; si no hay (null), se genera en el idioma del usuario.
    _DocumentLanguageLabel(row) {
      const lang = row && row.document_language;
      if (lang && DOCUMENT_LANGUAGE_LABELS[lang]) {
        return this.$t(DOCUMENT_LANGUAGE_LABELS[lang]);
      }
      return this.$t('document_language_user_default');
    },
    _FormatDate(date) {
      return date ? new Date(date).toLocaleDateString('es-ES') : '-';
    },

    _ReminderLabel(row) {
      if (!row || !row.emails_enabled) {
        return this.$t('no');
      }
      const days = Number(row.reminder_days_before);
      if (!Number.isInteger(days) || days < 0) {
        return this.$t('no');
      }
      if (days === 0) {
        return this.$t('reminder_same_day');
      }
      if (days === 1) {
        return this.$t('reminder_days_before_one');
      }
      return this.$t('reminder_days_before_n', [days]);
    },
  },
};
</script>

<style scoped>
.panel {
  background: white;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
.gap-3 { gap: 0.75rem; }

/* Borde inferior de la propia tabla (sin wrapper) */
.cert-table >>> .table {
  margin-bottom: 0 !important;
}
.cert-table >>> .table tbody tr:last-child td {
  border-bottom: 1px solid #dbdbdb !important;
}

.cert-actions {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
}

.emails-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.5rem;
  padding: 0.15rem 0.55rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.2;
}
.emails-status--yes {
  background: #e3fcec;
  color: #1f9d55;
}
.emails-status--no {
  background: #f3f4f6;
  color: #6b7280;
}

/* Icon button estilo Google Material — gris más intenso / pro */
.action-icon-btn {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  width: 2rem !important;
  height: 2rem !important;
  min-width: 2rem;
  min-height: 2rem;
  padding: 0 !important;
  margin: 0;
  border: 0 !important;
  border-radius: 50% !important;
  background-color: transparent !important;
  box-shadow: none !important;
  cursor: pointer;
  line-height: 1;
  vertical-align: middle;
  transition: background-color 150ms ease, color 150ms ease, transform 150ms ease;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
}
.action-icon-btn--edit,
.action-icon-btn--send {
  color: #4b5563 !important; /* gray-600, más presente */
}
.action-icon-btn--delete {
  color: #dc2626 !important; /* rojo más intenso */
}
.action-icon-btn--edit:hover,
.action-icon-btn--send:hover {
  background-color: #e5e7eb !important; /* gris suave, estilo Google */
  color: #111827 !important; /* casi negro */
}
.action-icon-btn--delete:hover {
  background-color: #fee2e2 !important;
  color: #b91c1c !important;
}
.action-icon-btn.is-disabled,
.action-icon-btn:disabled {
  opacity: 0.35 !important;
  cursor: not-allowed !important;
}
.action-icon-btn:active {
  transform: scale(0.94);
}
.action-icon-btn:focus {
  outline: none;
}
.action-icon-btn:focus-visible {
  box-shadow: 0 0 0 2px rgba(75, 85, 99, 0.35) !important;
}
.action-icon-btn >>> .icon {
  width: 1rem;
  height: 1rem;
}
</style>

<style lang="scss">
/* Modales certificación — estilo Facebook con colores EPC */
.cert-settings-modal .modal-background,
.send-test-modal .modal-background {
  background-color: rgba(0, 0, 0, 0.45);
}
.cert-settings-modal .animation-content,
.send-test-modal .animation-content {
  margin: 0 1rem;
}
.send-test-dialog {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.2), 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  font-family: inherit;
}
.send-test-dialog__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1rem 0.75rem;
  border-bottom: 1px solid #e5e7eb;
}
.send-test-dialog__title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.3;
}
.send-test-dialog__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 0;
  border-radius: 50%;
  background: #f3f4f6;
  color: #4b5563;
  cursor: pointer;
  transition: background-color 150ms ease, color 150ms ease;
}
.send-test-dialog__close:hover {
  background: #e5e7eb;
  color: #111827;
}
.send-test-dialog__body {
  padding: 1rem 1.15rem 0.5rem;
}
.send-test-dialog__text {
  margin: 0 0 0.85rem;
  font-size: 0.9375rem;
  line-height: 1.45;
  color: #374151;
}
.send-test-dialog__warning {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.7rem 0.8rem;
  border-radius: 8px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  color: #9a3412;
  font-size: 0.8125rem;
  line-height: 1.4;
}
.send-test-dialog__warning .icon {
  margin-top: 0.1rem;
  color: #ea580c;
}
.send-test-dialog__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1rem 1rem;
  border-top: 1px solid #e5e7eb;
  background: #fafafa;
}
.send-test-dialog__btn {
  min-width: 7rem;
  height: 2.25rem;
  padding: 0 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: 0;
  transition: background-color 150ms ease, color 150ms ease, box-shadow 150ms ease;
}
.send-test-dialog__btn--cancel {
  background: #e5e7eb;
  color: #1f2937;
}
.send-test-dialog__btn--cancel:hover {
  background: #d1d5db;
}
.send-test-dialog__btn--confirm {
  background: #5577BB; /* blue app */
  color: #fff;
  box-shadow: 0 1px 2px rgba(28, 69, 149, 0.25);
}
.send-test-dialog__btn--confirm:hover {
  background: #385DA5; /* blue-dark app */
}
</style>
